import type { Dispatch, SetStateAction } from "react"

import { useTranslation } from "react-i18next"
import { Button, Header, Message, Modal, Table } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback"

import { UserChip } from "components/lib/Chips"
import BaseChip from "components/lib/Chips/BaseChip"
import { AssessmentRoundLabel } from "components/lib/UI/AssessmentRoundLabel"
import util from "utils/utils"

import type { AssignableUser, LocalAssignments } from "./AssignmentDashboard"

type AssessmentRound = OpenAPI.Schemas["IdeaTemplate"]["assessments"]["rounds"][number]

type UserInspectModalProps = {
  inspectedAssessor: AssignableUser
  setInspectedAssessor: Dispatch<SetStateAction<AssignableUser | null>>
  localAssignments: LocalAssignments[string]["ideas"]
  setLocalAssignments: Dispatch<SetStateAction<LocalAssignments>>
  assessableIdeasById: Record<string, OpenAPI.Schemas["Idea"]>
  unassignAssessor: (ideaId: string, assessorId: string, assessmentRoundId: string) => void
  assessmentRoundMap: Record<string, AssessmentRound>
}

export const UserInspectModal = ({
  inspectedAssessor,
  setInspectedAssessor,
  localAssignments,
  setLocalAssignments,
  assessableIdeasById,
  unassignAssessor,
  assessmentRoundMap,
}: UserInspectModalProps) => {
  const { t } = useTranslation()

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={!!inspectedAssessor}
      onClose={() => setInspectedAssessor(null)}
    >
      {inspectedAssessor ? (
        <>
          <Modal.Header>
            Assessments for{" "}
            {inspectedAssessor.isEmailInvitee
              ? inspectedAssessor.email
              : inspectedAssessor.ownerAssessor.profile.fullName}
          </Modal.Header>
          <Modal.Content scrolling>
            {inspectedAssessor.isEmailInvitee ? (
              <BaseChip
                icon="mail"
                title={inspectedAssessor.email}
                content={`This is a pending invitation, the user has not yet signed up to ${util.appName()}.`}
              />
            ) : (
              <UserChip user={inspectedAssessor.ownerAssessor} />
            )}

            <Header as="h4">Draft assessments</Header>

            {!util.isEmpty(localAssignments) ? (
              <>
                <Message info>
                  These assignments are currently only in draft. Before the users is notified the assignments must first
                  be finalised and saved.
                </Message>
                <Table>
                  <Table.Body>
                    {Object.entries(localAssignments || {})
                      .filter(([ideaId, assignment]) => assignment?.assessmentRoundIds?.length > 0)
                      .map(([ideaId, assignment]) => (
                        <Table.Row key={ideaId}>
                          <Table.Cell collapsing>
                            <ImageWithFallback
                              avatar
                              style={{
                                minWidth: 35,
                                width: 35,
                                height: 25,
                                borderRadius: 4,
                                objectFit: "cover",
                              }}
                              src={util.ideaCoverImage(assessableIdeasById[ideaId], "200x200")}
                              fallbackSrc={util.ideaCoverImage()}
                            />
                          </Table.Cell>
                          <Table.Cell>{assessableIdeasById[ideaId]?.name || `Deleted ${t("generic.idea")}`}</Table.Cell>
                          <Table.Cell collapsing textAlign="right">
                            {assignment?.assessmentRoundIds?.map((assessmentRoundId) => {
                              const round = assessmentRoundMap[assessmentRoundId]
                              return round ? (
                                <AssessmentRoundLabel
                                  key={round.id}
                                  assessmentRound={round}
                                  onDelete={() => {
                                    setLocalAssignments((prevState) => {
                                      const newState = { ...prevState }

                                      newState[inspectedAssessor._id] = {
                                        ...newState[inspectedAssessor._id],
                                        ideas: {
                                          ...newState[inspectedAssessor._id].ideas,
                                          [ideaId]: {
                                            ...newState[inspectedAssessor._id].ideas[ideaId],
                                            assessmentRoundIds: newState[inspectedAssessor._id].ideas[
                                              ideaId
                                            ].assessmentRoundIds.filter((id) => id !== round.id),
                                          },
                                        },
                                      }

                                      const newRounds =
                                        newState?.[inspectedAssessor._id]?.ideas?.[ideaId].assessmentRoundIds
                                      if (newRounds.length === 0) {
                                        delete newState?.[inspectedAssessor._id]?.ideas?.[ideaId]
                                      }
                                      if (Object.keys(newState?.[inspectedAssessor._id]?.ideas || {}).length === 0) {
                                        delete newState?.[inspectedAssessor._id]
                                      }

                                      return newState
                                    })
                                  }}
                                />
                              ) : null
                            })}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                  </Table.Body>
                </Table>
              </>
            ) : (
              <Message>No draft assignments.</Message>
            )}
            <Header as="h4">Active assessments</Header>
            {inspectedAssessor.isEmailInvitee ? (
              <Message info>
                This user has been invited but is currently not registered. To manage their invitation, please refer to
                the "Dashboard" tab.
              </Message>
            ) : null}
            {!util.isEmpty(inspectedAssessor.assignments) ? (
              <Table>
                <Table.Body>
                  {Object.entries(inspectedAssessor.assignments || {})
                    .filter(([ideaId, assignment]) => assignment?.assessmentRoundIds?.length > 0)
                    .map(([ideaId, assignment]) => (
                      <Table.Row key={ideaId}>
                        <Table.Cell collapsing>
                          <ImageWithFallback
                            avatar
                            style={{
                              minWidth: 35,
                              width: 35,
                              height: 25,
                              borderRadius: 4,
                              objectFit: "cover",
                            }}
                            src={util.ideaCoverImage(assessableIdeasById[ideaId], "200x200")}
                            fallbackSrc={util.ideaCoverImage()}
                          />
                        </Table.Cell>
                        <Table.Cell>{assessableIdeasById[ideaId]?.name || `Deleted ${t("generic.idea")}`}</Table.Cell>
                        <Table.Cell collapsing textAlign="right">
                          {assignment?.assessmentRoundIds?.map((assessmentRoundId) => {
                            const round = assessmentRoundMap[assessmentRoundId]
                            return round ? (
                              <AssessmentRoundLabel
                                key={round.id}
                                assessmentRound={round}
                                onDelete={
                                  inspectedAssessor.isEmailInvitee
                                    ? undefined
                                    : () => {
                                        unassignAssessor(ideaId, inspectedAssessor._id, round.id)
                                      }
                                }
                              />
                            ) : null
                          })}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                </Table.Body>
              </Table>
            ) : (
              <Message>No active assessments.</Message>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setInspectedAssessor(null)}>Close</Button>
          </Modal.Actions>
        </>
      ) : null}
    </Modal>
  )
}
